import React from 'react';
import { Alert, Box, Grid, Paper, Stack, Typography } from '@mui/material';
import type { Cir } from '../../types/e2i';
import { CIR_STATUS } from '../../types/e2i';
import { draftLabel, formatAmount, formatDate, formatDateTime } from '../../utils/e2i';
import AttachmentsPanel from './AttachmentsPanel';
import CirActionBar from './CirActionBar';
import InfoField from './InfoField';
import SapLine from './SapLine';
import { StatusChip } from './StatusChip';
import type { Notify } from './workspace';

interface Props {
  cir: Cir;
  currency: string;
  notify: Notify;
  onChanged: () => Promise<void>;
  onEdit: () => void;
}

/** Where an invoice request stands in SAP: its A/R invoice draft, then the invoice finance added. */
export const CirSapState: React.FC<{ cir: Cir }> = ({ cir }) => {
  const sap = cir.sap;
  if (!sap) return null;
  if (sap.invoiceNum || sap.invoiceEntry) {
    return <SapLine status="Posted">SAP A/R invoice {sap.invoiceNum || sap.invoiceEntry}{sap.invoiceDate ? ` of ${formatDate(sap.invoiceDate)}` : ''}</SapLine>;
  }
  if (sap.error && cir.status !== 'Booked') return <SapLine status="Failed" error={sap.error}>SAP refused the A/R invoice draft</SapLine>;
  if (sap.draftEntry && cir.status === 'Booked') {
    return <SapLine status="Waiting">A/R invoice draft {draftLabel(sap.draftNum, sap.draftEntry)} is in SAP: finance adds it through its approval. PRPO sees the invoice within a few minutes of that.</SapLine>;
  }
  return null;
};

/** One invoice request in the workspace: lines, references, where it stands, its actions and files. */
const CirCard: React.FC<Props> = ({ cir, currency, notify, onChanged, onEdit }) => {
  const p = cir.permissions || ({} as Cir['permissions']);
  const canFiles = cir.status !== 'Cancelled' && (p.canEdit || p.canBook || p.canSetClientDate);
  return (
    <Paper sx={{ p: 2 }} component="article" aria-label={`Invoice request ${cir.cirNo || ''}`.trim()}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>{cir.cirNo || 'Invoice request'}</Typography>
        <StatusChip status={CIR_STATUS[cir.status]} fallback={cir.status} />
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2">
          {formatAmount(cir.subtotal)} + VAT {formatAmount(cir.vatAmount)} = <strong>{formatAmount(cir.total)} {currency}</strong>
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        {cir.returnNote && (cir.status === 'Open' || cir.status === 'Submitted') ? <Alert severity="warning">Returned by finance: {cir.returnNote}</Alert> : null}
        <CirSapState cir={cir} />

        <Stack component="ul" spacing={0.25} sx={{ m: 0, p: 0, listStyle: 'none' }} aria-label="Lines">
          {cir.lines.map((l) => (
            <Stack component="li" key={`${l.lineNum}-${l.jobLineId}`} direction="row" spacing={1} sx={{ borderBottom: '1px solid #eceff1', py: 0.25 }}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{l.lineNum}. {l.description}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatAmount(l.amount)}</Typography>
            </Stack>
          ))}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}><InfoField label="Customer VAT number" value={cir.customerTrn} /></Grid>
          <Grid item xs={12} sm={6} md={3}><InfoField label="Client PO" value={cir.customerPoRef} /></Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoField label="Service period" value={cir.periodFrom || cir.periodTo ? `${formatDate(cir.periodFrom) || '…'} to ${formatDate(cir.periodTo) || '…'}` : ''} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}><InfoField label="Submitted to the client" value={formatDate(cir.clientSubmissionDate)} /></Grid>
          {cir.remarks ? <Grid item xs={12}><InfoField label="Remarks" value={cir.remarks} multiline /></Grid> : null}
        </Grid>

        <Typography variant="caption" color="text.secondary">
          {[
            cir.submittedAt ? `Submitted${cir.submittedByName ? ` by ${cir.submittedByName}` : ''} ${formatDateTime(cir.submittedAt)}` : '',
            cir.bookedAt ? `booked${cir.bookedByName ? ` by ${cir.bookedByName}` : ''} ${formatDateTime(cir.bookedAt)}` : '',
          ].filter(Boolean).join(' · ')}
        </Typography>

        <CirActionBar cir={cir} onChanged={() => onChanged()} onEdit={onEdit} notify={notify} />

        <AttachmentsPanel
          id={`e2i-cir-${cir.cirId}-files`}
          entity="cir"
          entityId={cir.cirId}
          attachments={cir.attachments || []}
          canUpload={canFiles}
          canDelete={canFiles && cir.status !== 'Posted'}
          onChanged={onChanged}
          title="Supporting files"
        />
      </Stack>
    </Paper>
  );
};

export default CirCard;
